import { createContext, useEffect, useMemo } from "react";
import Resumable from "resumablejs";
import { useSessionKf } from "components/auth/lib/useSessionKf";
import { endpointsAssets } from "lib/actions/endpoints/endpointsAssets";
import { fetchApi, prependApiOrigin } from "lib/actions/fetcher";
import {
  ActionsDispatchUnknown,
  useActionsReducer,
} from "lib/hooks/useActionsReducer";
import { UploadStatus } from "lib/types/UploadStatus";

export const UploadContext =
  createContext<ReturnType<typeof useProvider>>(null);

export const UploadProvider = (p: { children: React.ReactNode }) => {
  const upload = useProvider();

  return <UploadContext value={upload}>{p.children}</UploadContext>;
};

export enum EndpointKey {
  None,
  ProjectFiles,
}

export type Upload = {
  id: string;
  endpoint?: EndpointKey;
  status: UploadStatus;
  progress: number;
  file: File;
};

type UploadEndpoint = {
  key: EndpointKey;
  urlStart?: string;
  urlUpload: string;
  urlComplete: string;
  r: Resumable;
};

type State = {
  endpoints: UploadEndpoint[];
  uploads: Upload[];
};

const NEW_FILE_ID = "#NEW#";
const initialState: State = {
  endpoints: [],
  uploads: [],
};
const getEndpoint = (state: State, key: EndpointKey): UploadEndpoint => {
  return state.endpoints.find((e) => e.key === key);
};
const getUploadEndpoint = (state: State, id: string): UploadEndpoint => {
  const upload = state.uploads.find((s) => s.id === id);
  return upload && state.endpoints.find((e) => e.key === upload.endpoint);
};
const getUpload = (state: State, id: string): Upload => {
  return state.uploads.find((u) => u.id === id);
};
const getResumableFile = (
  state: State,
  id: string,
): Resumable.ResumableFile => {
  const endpoint = getUploadEndpoint(state, id);
  return endpoint?.r.files.find((f) => f.uniqueIdentifier === id);
};
const createUpload = (
  id: string,
  endpointKey: EndpointKey,
  file: File,
): Upload => {
  return {
    id: id,
    endpoint: endpointKey,
    status: UploadStatus.preparing,
    progress: 0,
    file,
  };
};
const updateUpload = (
  state: State,
  id: string,
  status?: UploadStatus,
): Upload => {
  const upload = getUpload(state, id);
  if (!upload) return null;
  //find file in resumablejs
  const file = getResumableFile(state, id);
  const progress = file?.progress(false) ?? 0;
  //create new state
  const updated: Upload = {
    ...upload,
    progress,
    ...(status && { status }), //dont change if not present
  };
  return updated;
};

const actions = {
  callback: (state: State, callback: (state: State) => void): State => {
    callback && callback(state);
    return state;
  },
  setEndpoint: (
    state: State,
    dispatch: ActionsDispatchUnknown<State>,
    key: EndpointKey,
    startUrl: string,
    uploadUrl: string,
    completeUrl: string,
  ): State => {
    if (getEndpoint(state, key)) return state;
    const r = new Resumable({
      target: prependApiOrigin(uploadUrl),
      chunkSize: 25 * 1024 * 1024, //25MiB
      forceChunkSize: true,
      simultaneousUploads: 2,
      method: "octet",
      uploadMethod: "PUT",
      testChunks: true,
      identifierParameterName: "uploadId",
      fileParameterName: "file",
      chunkNumberParameterName: "chunkNumber",
      chunkSizeParameterName: "chunkSize",
      currentChunkSizeParameterName: "chunkSize",
      fileNameParameterName: "fileName",
      totalSizeParameterName: "totalSize",
      generateUniqueIdentifier: () => NEW_FILE_ID,
      headers: {
        Authorization: null,
        "Cache-Control": "no-cache",
      },
    });
    r.on("fileProgress", (file: Resumable.ResumableFile) => {
      if (!file.isUploading()) return;
      const id = file.uniqueIdentifier;
      dispatch([actions.updateUpload, id, UploadStatus.uploading]);
    });
    r.on("fileError", (file: Resumable.ResumableFile, message: string) => {
      console.error(file, message);
      const id = file.uniqueIdentifier;
      dispatch([actions.updateUpload, id, UploadStatus.error]);
    });
    r.on("fileSuccess", async (file: Resumable.ResumableFile) => {
      const id = file.uniqueIdentifier;
      //post upload complete
      let success = true;
      if (completeUrl) {
        try {
          const response = await fetchApi({
            method: "POST",
            url: completeUrl,
            body: {
              uploadId: id,
            },
          });
          if (response.status >= 400) success = false;
        } catch {
          success = false;
        }
      }
      dispatch([
        actions.updateUpload,
        id,
        success ? UploadStatus.complete : UploadStatus.error,
      ]);
    });
    return {
      ...state,
      endpoints: [
        ...state.endpoints,
        {
          key,
          ...(startUrl && { urlStart: startUrl }),
          urlUpload: uploadUrl,
          urlComplete: completeUrl,
          r,
        },
      ],
    };
  },

  registerFile: (
    state: State,
    endpointKey: EndpointKey,
    id: string,
    file: File,
  ): State => {
    if (!id || !file) return state;
    const endpoint = getEndpoint(state, endpointKey);
    if (!endpoint) throw new Error("Endpoint not configured.");
    //create new state
    return {
      ...state,
      uploads: [...state.uploads, createUpload(id, endpoint.key, file)],
    };
  },

  uploadFile: (state: State, id: string): State => {
    const upload = state.uploads.find((u) => u.id === id);
    const endpoint = getUploadEndpoint(state, id);
    if (!upload || !endpoint) return state;
    //add to resumablejs
    endpoint.r.addFile(upload.file);
    const rFile = endpoint.r.files.find(
      (f) => f.uniqueIdentifier === NEW_FILE_ID,
    );
    rFile.uniqueIdentifier = id;
    //create new state
    return actions.updateUpload(state, id, UploadStatus.queued);
  },

  pauseFile: (state: State, id: string): State => {
    const f = getResumableFile(state, id);
    if (!f) return state;
    f.abort();
    //create new state
    return actions.updateUpload(state, id, UploadStatus.paused);
  },

  resumeFile: (state: State, id: string): State => {
    const f = getResumableFile(state, id);
    if (!f) return state;
    f.retry();
    //create new state
    return actions.updateUpload(state, id, UploadStatus.queued);
  },

  errorFile: (state: State, id: string): State => {
    const f = getResumableFile(state, id);
    if (f) f.cancel();
    //create new state
    return actions.updateUpload(state, id, UploadStatus.error);
  },

  cancelFile: (state: State, id: string): State => {
    const f = getResumableFile(state, id);
    if (f) f.cancel();
    //create new state
    const uploads = state.uploads.filter((u) => u.id !== id);
    return {
      ...state,
      uploads,
    };
  },

  updateUpload: (state: State, id: string, status?: UploadStatus): State => {
    if (!id) return state;
    const updated = updateUpload(state, id, status);
    if (!updated) return state;
    //create new state
    const uploads = state.uploads.map((s) => (s.id === id ? updated : s));
    return {
      ...state,
      uploads,
    };
  },

  changeId: (state: State, id: string, newId: string): State => {
    if (id == null || newId == null) return state;
    const upload = getUpload(state, id);
    if (!upload) return state;
    //create new state
    const uploads = state.uploads.map((s) =>
      s.id === id ? { ...upload, id: newId } : s,
    );
    return {
      ...state,
      uploads,
    };
  },
};

const useProvider = () => {
  const { session } = useSessionKf();
  const [state, dispatch] = useActionsReducer(actions, initialState);

  const token = session?.accessToken;

  const provider = useMemo(() => {
    const p = {
      uploads: state?.uploads ?? [],

      getEndpointUploads: (endpointKey: EndpointKey) =>
        state?.uploads.filter((s) => s.endpoint === endpointKey),

      setEndpoint: (
        key: EndpointKey,
        startUrl: string,
        chunksUrl: string,
        completeUrl: string,
      ) => {
        dispatch([
          actions.setEndpoint,
          dispatch,
          key,
          startUrl,
          chunksUrl,
          completeUrl,
        ]);
      },

      /**
       * @param id Reference for upload throughout lifecycle of upload; registration to processing
       * @throws file or id not provided */
      addFile: (endpointKey: EndpointKey, id: string, file: File) => {
        if (!file || !id) throw new Error("File and id required.");
        dispatch([actions.registerFile, endpointKey, id, file]);
      },

      /**
       * @throws File not registered */
      startFile: (id: string) => {
        const callback = async (state: State) => {
          const upload = getUpload(state, id);
          if (!upload) throw new Error("File not registered.");

          const endpoint = getEndpoint(state, upload.endpoint);
          if (endpoint.urlStart) {
            //notify server of a file coming its way
            const { chunkSize } = endpoint.r.opts;
            const {
              size: totalSize,
              name: fileName,
              type: mimeType,
            } = upload.file;
            try {
              await fetchApi({
                method: "POST",
                url: endpoint.urlStart,
                body: {
                  chunkSize,
                  fileName,
                  totalSize,
                  mimeType,
                },
                query: {
                  uploadId: id,
                },
              });
            } catch {
              dispatch([actions.updateUpload, id, UploadStatus.error]);
              return;
            }
          }
          dispatch([actions.uploadFile, id]);
          dispatch([actions.resumeFile, id]);
        };
        dispatch([actions.callback, callback]);
      },

      pauseFile: (id: string) => {
        dispatch([actions.pauseFile, id]);
      },

      resumeFile: (id: string) => {
        dispatch([actions.resumeFile, id]);
      },

      retryFile: (id: string) => {
        dispatch([actions.resumeFile, id]);
      },

      cancelFile: (id: string) => {
        dispatch([actions.cancelFile, id]);
      },

      errorFile: (id: string) => {
        dispatch([actions.errorFile, id]);
      },

      changeUploadId: (id: string, newId: string) => {
        if (id == null || newId == null)
          throw new Error("Id and newId required.");
        dispatch([actions.changeId, id, newId]);
      },
    };
    return p;
  }, [state]);

  useEffect(() => {
    //set auth token
    if (!token) return;
    state.endpoints.forEach(
      (e) => (e.r.opts.headers["Authorization"] = `Bearer ${token}`),
    );
  }, [token, state.endpoints]);

  useEffect(() => {
    //init endpoints
    provider.setEndpoint(
      EndpointKey.ProjectFiles,
      endpointsAssets.uploadStart,
      endpointsAssets.uploadChunk,
      endpointsAssets.uploadComplete,
    );
  }, []);

  return provider;
};
